import React, { useState, useEffect } from "react";
import { Card, Modal, Typography, Space, Tag, Button, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { formatLkr } from "../../../utils/currency";
import { MenuItem } from "../RestaurantPOS";
import { canViewPrices } from '../../../utils/permissions';


export const MenuItemDetailsDialog: React.FC<{
  item: MenuItem | null;
  visible: boolean;
  onClose: () => void;
  canManage: boolean;
  onEdit: (item: MenuItem) => void;
  onDelete: (id: string) => void;
}> = ({ item, visible, onClose, canManage, onEdit, onDelete }) => {
  const [displayItem, setDisplayItem] = useState<MenuItem | null>(item);

  useEffect(() => {
    if (item) {
      setDisplayItem(item);
    }
  }, [item]);

  if (!displayItem) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      title={null}
      width={500}
      styles={{ body: { padding: 0, overflow: "hidden", borderRadius: 8 } }}
    >
      <div style={{ display: "flex", flexDirection: "column" }}>
        {displayItem.image_url ? (
          <img
            src={displayItem.image_url}
            alt={displayItem.name}
            style={{
              width: "100%",
              height: 250,
              objectFit: "cover",
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: 250,
              backgroundColor: "#f0f2f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
            }}
          >
            <Typography.Text type="secondary">No Image</Typography.Text>
          </div>
        )}
        <div style={{ padding: "24px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <Typography.Title
                level={4}
                style={{ margin: 0, fontWeight: 600 }}
              >
                {displayItem.name}
              </Typography.Title>
              <Tag
                color={displayItem.is_available ? "green" : "red"}
                style={{ margin: 0 }}
              >
                {displayItem.is_available ? "Available" : "Unavailable"}
              </Tag>
            </div>
            <Typography.Title
              level={4}
              style={{
                margin: 0,
                color: "#2e7d32",
                whiteSpace: "nowrap",
                paddingLeft: 12,
              }}
            >
              {canViewPrices() ? formatLkr(displayItem.price_dine_in) : "***"}
            </Typography.Title>
          </div>

          <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dine-In</Typography.Text>
              <br />
              <Typography.Text strong>{canViewPrices() ? formatLkr(displayItem.price_dine_in) : "***"}</Typography.Text>
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Take Away</Typography.Text>
              <br />
              <Typography.Text strong>{canViewPrices() ? formatLkr(displayItem.price_take_away) : "***"}</Typography.Text>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginBottom: 16,
            }}
          >
            {(displayItem.category_name || displayItem.is_featured || displayItem.kitchen_group_name) && (
              <Space size={[0, 8]} wrap>
                {displayItem.category_name && (
                  <Tag color="blue">{displayItem.category_name}</Tag>
                )}
                {displayItem.kitchen_group_name && (
                  <Tag color="orange">{displayItem.kitchen_group_name}</Tag>
                )}
                {displayItem.is_featured && <Tag color="gold">Featured</Tag>}
              </Space>
            )}

            {displayItem.badge && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Highlights:
                </Typography.Text>
                <Space size={[0, 8]} wrap>
                  {displayItem.badge.split(",").map((badgeStr, index) => {
                    const trimmedBadge = badgeStr.trim();
                    return trimmedBadge ? (
                      <Tag
                        key={index}
                        color="purple"
                        style={{ marginRight: 8 }}
                      >
                        {trimmedBadge}
                      </Tag>
                    ) : null;
                  })}
                </Space>
              </div>
            )}
          </div>

          {displayItem.description && (
            <Typography.Paragraph
              style={{
                fontSize: 15,
                color: "#555",
                lineHeight: 1.6,
                marginBottom: 24,
              }}
            >
              {displayItem.description}
            </Typography.Paragraph>
          )}

          {canManage && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
                marginTop: 8,
                paddingTop: 16,
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  onClose();
                  onEdit(displayItem);
                }}
              >
                Edit Item
              </Button>
              <Popconfirm
                title="Deactivate this menu item?"
                onConfirm={() => {
                  onClose();
                  onDelete(displayItem.id);
                }}
              >
                <Button danger icon={<DeleteOutlined />}>
                  Deactivate Item
                </Button>
              </Popconfirm>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export const MenuItemCard: React.FC<{
  item: MenuItem;
  canManage: boolean;
  onEdit: (item: MenuItem) => void;
  onDelete: (id: string) => void;
  onClick: (item: MenuItem) => void;
}> = ({ item, canManage, onEdit, onDelete, onClick }) => (
  <Card
    hoverable
    style={{ height: 400, display: "flex", flexDirection: "column" }}
    bodyStyle={{
      flex: 1,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    }}
    onClick={() => onClick(item)}
    cover={
      item.image_url ? (
        <img
          alt={item.name}
          src={item.image_url}
          style={{ height: 180, objectFit: "cover" }}
        />
      ) : (
        <div
          style={{
            height: 180,
            backgroundColor: "#f0f2f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography.Text type="secondary">No Image</Typography.Text>
        </div>
      )
    }
  >
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: 16,
            lineHeight: 1.2,
            paddingRight: 8,
          }}
        >
          {item.name}
        </span>
        <Typography.Text
          strong
          style={{ color: "#2e7d32", whiteSpace: "nowrap" }}
        >
          {canViewPrices() ? formatLkr(item.price_dine_in) : ""}
        </Typography.Text>
      </div>
      {item.description && (
        <Typography.Paragraph
          type="secondary"
          ellipsis={{ rows: 2, tooltip: item.description }}
          style={{ marginBottom: 8, fontSize: 13 }}
        >
          {item.description}
        </Typography.Paragraph>
      )}
      <div
        style={{
          marginTop: "auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 8,
        }}
      >
        <Space size={[0, 4]} wrap style={{ flex: 1 }}>
          {item.category_name && (
            <Tag color="blue" style={{ margin: 0 }}>
              {item.category_name}
            </Tag>
          )}
          {item.is_featured && (
            <Tag color="gold" style={{ margin: 0 }}>
              Featured
            </Tag>
          )}
          <Tag
            color={item.is_available ? "green" : "red"}
            style={{ margin: 0 }}
          >
            {item.is_available ? "Available" : "Unavailable"}
          </Tag>
        </Space>
        {canManage && (
          <div onClick={(e) => e.stopPropagation()}>
            <Space size={4}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEdit(item)}
              />
              <Popconfirm
                title="Deactivate this menu item?"
                onConfirm={() => onDelete(item.id)}
              >
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Space>
          </div>
        )}
      </div>
    </div>
  </Card>
);
